// Shared "already alerted" ledger for QR order requests.
//
// A pending OrderRequest can reach this terminal on two independent intake
// transports: the real-time Pusher/LAN push (handled in App.vue) and the 60s
// Orders Hub poll (handled in useOrdersHubIngestion). The backend broadcasts a
// pending request exactly once, but the push can be delayed past a poll tick —
// so the poll alerts first, then the delayed push arrives. The push path can't
// use a store lookup to tell new-from-restate (onSyncTerminals inserts the
// record before the callback runs), so both transports route their alert
// decision through this single id-keyed ledger to fire the sound/toast exactly
// once per request, regardless of which transport wins the race.
const alertedIds = new Set<number>()

/**
 * Claim the right to alert for this request id. Returns true if the id was
 * newly recorded (the caller SHOULD raise the alert) and false if it had
 * already been alerted (the caller must stay silent). Mark-and-check in one
 * call so the two transports can't both pass the gate for the same id.
 */
export function markOrderRequestAlerted(id: number): boolean {
  if (alertedIds.has(id)) return false
  alertedIds.add(id)
  return true
}

/**
 * Forget an id when a 'delete' push retires its request. Note the poll's
 * GC-by-absence path (OrdersHubService) resolves requests without routing
 * through here, so the ledger isn't strictly bounded to currently-pending ids:
 * poll-resolved ids linger for the session. That's a bounded, session-scoped
 * set of ints — ids are never reused — so it has no correctness impact.
 */
export function clearOrderRequestAlerted(id: number): void {
  alertedIds.delete(id)
}
